import React, { memo } from 'react';
import Link from 'next/link';
import { useCart } from '../../context/CartContext';
import { toast } from 'react-hot-toast';
import { ShoppingBagIcon } from '@heroicons/react/24/outline';
import { HeartIcon } from '@heroicons/react/24/outline';
import { buildImageUrl } from '../../utils/imageUtils';
import SafeImage from '../ui/SafeImage';

const CustomerProductCard = memo(({ product }) => {
  const { addToCart } = useCart();

  if (!product || !product.name) return null;

  const productUrl = `/products/${product.slug || product._id || product.id}`;
  const currentPrice = product.pricing?.price || product.price || 0;
  const comparePrice = product.comparePrice || product.compareAtPrice || product.oldPrice;
  const isOutOfStock = product.inventory?.stockStatus === 'out-of-stock';
  const discountPercentage = comparePrice && comparePrice > currentPrice
    ? Math.round(((comparePrice - currentPrice) / comparePrice) * 100)
    : 0;

  const primaryImage = product.images?.[0]?.url || product.images?.[0]?.thumbnail || '/placeholder.jpg';

  const formatPrice = (price) => {
    if (!price || price === 0) return '0 HTG';
    return new Intl.NumberFormat('fr-FR').format(price) + ' HTG';
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOutOfStock) return;

    try {
      addToCart(product, 1);
      toast.success(`${product.name} ajouté!`, {
        position: 'bottom-center',
        style: { background: '#1b4332', color: 'white' }
      });
    } catch {
      toast.error("Erreur", {
        position: 'bottom-center',
      });
    }
  };

  const categoryName = product.category?.name || 'Collection';

  return (
    <div className="group flex flex-col bg-theme-card rounded-[2.5rem] overflow-hidden transition-all duration-700 hover:shadow-2xl relative border border-theme">
      {/* Editorial Image Container - Truly Flush with Separator Line */}
      <Link href={productUrl} className="block relative overflow-hidden h-80 sm:h-96 bg-gray-50 dark:bg-[#1a1c26] group/image w-full border-b border-theme">
        <div className="relative w-full h-full p-1">
          <SafeImage
            src={buildImageUrl(primaryImage)}
            alt={product.name}
            fill
            className="w-full h-full object-contain transition-transform duration-1000 group-hover:scale-110"
          />
        </div>
        
        {/* Badges - Top Row */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-center z-20">
          {discountPercentage > 0 ? (
            <div className="bg-[#e8f5e9]/90 dark:bg-green-900/40 text-[#2d6a4f] dark:text-green-400 text-[10px] font-bold px-4 py-2 rounded-full shadow-sm backdrop-blur-sm">
              -{discountPercentage}%
            </div>
          ) : (
            <div className="bg-white/80 dark:bg-[#2d3142]/80 backdrop-blur-md text-theme-primary text-[10px] font-bold px-4 py-2 rounded-full shadow-sm">
              Nouveau
            </div>
          )}
          
          <button className="p-2 bg-white/80 dark:bg-[#2d3142]/80 backdrop-blur-md rounded-full shadow-sm hover:scale-110 transition-transform">
            <HeartIcon className="w-4 h-4 text-theme-tertiary hover:text-red-500 transition-colors" />
          </button>
        </div>

        {isOutOfStock && (
          <div className="absolute inset-0 bg-white/40 dark:bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-10 font-bold text-theme-primary uppercase tracking-widest text-xs">
            Épuisé
          </div>
        )}
      </Link>

      {/* Info Section */}
      <div className="flex flex-col p-6 pt-6">
        <div className="mb-4">
          <p className="text-[11px] text-primary-500 font-bold uppercase tracking-wide mb-1">
            {categoryName}
          </p>
          <Link href={productUrl}>
            <h3 className="text-lg font-bold text-theme-primary hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-300 line-clamp-1">
              {product.name}
            </h3>
          </Link>
          <p className="text-lg font-bold text-primary-500 mt-1">
            {formatPrice(currentPrice)}
            {discountPercentage > 0 && (
              <span className="ml-2 text-xs text-theme-tertiary line-through font-normal">
                {formatPrice(comparePrice)}
              </span>
            )}
          </p>
        </div>

        <div className="mt-auto">
          <button 
            onClick={handleAddToCart}
            disabled={isOutOfStock}
            className="w-full py-4 bg-[#262626] dark:bg-primary-500 text-white dark:text-gray-900 text-sm font-bold rounded-full hover:bg-black dark:hover:bg-primary-600 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Acheter
          </button>
        </div>
      </div>
    </div>
  );
});

CustomerProductCard.displayName = 'CustomerProductCard';

export default CustomerProductCard;
